import { Controller } from "@hotwired/stimulus"
import { formatShortcut, shortcuts } from "../keyboard/commands"
import { collectDomCommands } from "../keyboard/dom_commands"
import { DomShortcutHints } from "../keyboard/dom_shortcut_hints"

export default class extends Controller {
  static targets = ["dialog", "input", "results", "empty", "trigger", "hint", "preferences"]

  connect() {
    if (!this.hasTriggerTarget) return
    this.emptyMessage = this.emptyTarget.dataset.emptyMessage || this.emptyTarget.textContent
    shortcuts.connect(this.element)
    this.unregisterProvider = shortcuts.addProvider(() => collectDomCommands(this.element, { enabled: shortcuts.enabled }))
    this.unregisterOpen = shortcuts.register({
      id: "shortcuts.open", label: this.triggerTarget.getAttribute("aria-label"),
      element: this.triggerTarget, action: () => this.open()
    })
    this.hints = new DomShortcutHints(this.element)
    this.hintsObserver = new MutationObserver(() => {
      if (this.hintsFrame) return
      this.hintsFrame = requestAnimationFrame(() => { this.hintsFrame = null; this.refreshHints() })
    })
    this.unsubscribeHints = shortcuts.subscribe(() => this.refreshHints())
    this.updatePreferences()
  }

  preferencesTargetConnected() {
    if (shortcuts.root === this.element) this.updatePreferences()
  }

  updatePreferences() {
    let bindings = {}
    try { bindings = JSON.parse(this.preferencesTarget.dataset.bindings || "{}") } catch { /* Keep default bindings if stale cached markup is malformed. */ }
    shortcuts.setPreferences({ enabled: this.preferencesTarget.dataset.enabled !== "false", bindings })
    this.element.dataset.keyboardShortcutsEnabled = String(shortcuts.enabled)
    const keys = shortcuts.bindingFor("shortcuts.open")
    this.hintTarget.textContent = formatShortcut(keys)
    this.hintTarget.hidden = !keys
    if (keys) this.triggerTarget.setAttribute("aria-keyshortcuts", keys.replace("mod", shortcuts.mac ? "Meta" : "Control"))
    else this.triggerTarget.removeAttribute("aria-keyshortcuts")
    this.refreshHints()
    document.dispatchEvent(new CustomEvent("keyboard-shortcuts:changed"))
  }

  refreshHints() {
    if (!this.hints || !this.element.isConnected) return
    this.hintsObserver.disconnect()
    this.hints.render([...collectDomCommands(this.element, { enabled: shortcuts.enabled }), ...shortcuts.commands.values()])
    this.hintsObserver.observe(this.element, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ["data-command", "data-command-label", "data-shortcut-hint-managed", "data-shortcuts", "class", "disabled", "hidden", "aria-disabled", "aria-busy", "href", "type", "method"]
    })
  }

  open(event) {
    event?.preventDefault()
    if (this.dialogTarget.open) return
    // Safari button clicks do not necessarily move document.activeElement.
    this.returnFocus = event?.currentTarget || document.activeElement
    const navigationActions = new Set()
    this.commands = shortcuts.available(this.returnFocus).filter(command => {
      if (command.id === "shortcuts.open" || !command.keys) return false
      if (!command.navigationKey) return true
      const key = JSON.stringify([command.id, command.navigationKey])
      if (navigationActions.has(key)) return false
      navigationActions.add(key)
      return true
    })
    this.inputTarget.value = ""
    this.render()
    this.dialogTarget.showModal()
    this.inputTarget.focus()
  }

  render() {
    const query = this.inputTarget.value.trim().toLocaleLowerCase()
    this.filtered = this.commands.filter(command => command.label.toLocaleLowerCase().includes(query))
    this.resultsTarget.replaceChildren(...this.filtered.map((command, index) => {
      const item = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.className = "keyboard-command"
      button.dataset.commandIndex = index
      button.dataset.action = "keyboard-shortcuts#run"
      const label = document.createElement("span")
      label.textContent = command.label
      button.append(label)
      if (command.keys) {
        const hint = document.createElement("kbd")
        hint.textContent = formatShortcut(command.keys)
        button.append(hint)
      }
      item.append(button)
      return item
    }))
    this.emptyTarget.textContent = shortcuts.enabled ? this.emptyMessage : this.emptyTarget.dataset.disabledMessage || this.emptyMessage
    this.emptyTarget.hidden = this.filtered.length > 0
  }

  run(event) {
    this.runCommand(this.filtered[Number(event.currentTarget.dataset.commandIndex)])
  }

  runCommand(command) {
    this.close()
    this.returnFocus?.focus({ preventScroll: true })
    shortcuts.execute(command, this.returnFocus)
  }

  navigate(event) {
    if (event.defaultPrevented || event.isComposing || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }
    const buttons = [...this.resultsTarget.querySelectorAll("button")]
    if (!buttons.length) return
    if (event.key === "Enter" && event.target === this.inputTarget) {
      event.preventDefault()
      // Newly rendered results may not have Stimulus click handlers yet.
      this.runCommand(this.filtered[0])
    } else if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault()
      const current = buttons.indexOf(document.activeElement)
      const next = current === -1 ? (event.key === "ArrowDown" ? 0 : buttons.length - 1)
        : (current + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length
      buttons[next].focus()
    }
  }

  backdrop(event) {
    if (event.target !== this.dialogTarget) return
    const rect = this.dialogTarget.getBoundingClientRect()
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close()
  }

  close() {
    this.dialogTarget.close()
  }

  closed() {
    if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true })
  }

  beforeCache() {
    this.close()
    this.commands = []
    this.resultsTarget.replaceChildren()
    if (this.hintsFrame) cancelAnimationFrame(this.hintsFrame)
    this.hintsFrame = null
    this.hintsObserver?.disconnect()
    this.hints?.disconnect()
  }

  disconnect() {
    this.hintsObserver?.disconnect()
    if (this.hintsFrame) cancelAnimationFrame(this.hintsFrame)
    this.unsubscribeHints?.()
    this.hints?.disconnect()
    this.unregisterProvider?.()
    this.unregisterOpen?.()
    shortcuts.disconnect(this.element)
  }
}
